import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import SectionHeading from "../SectionHeading";
import { SERVICE_CATEGORIES } from "../../data/site";

export const Services = () => {
  const [active, setActive] = useState(0);
  const cat = SERVICE_CATEGORIES[active];
  const Icon = cat.icon;

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-navy-deep">
      <div className="container-x">
        <SectionHeading
          overline="What We Do"
          title="Full-spectrum technology services"
          subtitle="Consulting, engineering, cloud, AI & automation, security and digital growth — organised into eight capability areas so you always know exactly what ATS can build, run and grow for you."
        />

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* tab list */}
          <div className="lg:col-span-5 flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0">
            {SERVICE_CATEGORIES.map((c, i) => {
              const CIcon = c.icon;
              const isActive = i === active;
              return (
                <button
                  key={c.id}
                  onClick={() => setActive(i)}
                  data-testid={`service-tab-${c.id}`}
                  className={`group shrink-0 lg:shrink text-left rounded-2xl px-5 py-4 border transition-colors duration-300 flex items-center gap-4 min-w-[240px] lg:min-w-0 ${
                    isActive
                      ? "bg-[#F26522]/15 border-[#F26522]/50"
                      : "bg-white/[0.02] border-white/10 hover:bg-white/[0.05]"
                  }`}
                >
                  <span
                    className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 transition-colors duration-300 ${
                      isActive ? "bg-[#F26522] text-white" : "bg-white/5 text-slate-300 group-hover:text-white"
                    }`}
                  >
                    <CIcon size={20} />
                  </span>
                  <span className={`font-head font-semibold text-sm md:text-base ${isActive ? "text-white" : "text-slate-300"}`}>
                    {c.title}
                  </span>
                </button>
              );
            })}
          </div>

          {/* detail panel */}
          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4 }}
                data-testid="service-detail"
                className="glass rounded-3xl p-8 md:p-10 h-full relative overflow-hidden"
              >
                <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-brand-orange/10 blur-3xl" />
                <div className="relative">
                  <div className="w-14 h-14 rounded-2xl bg-brand-orange/15 grid place-items-center mb-6">
                    <Icon size={26} className="text-brand-orange" />
                  </div>
                  <h3 className="font-head text-2xl md:text-3xl font-semibold text-white">{cat.title}</h3>
                  <p className="mt-3 text-slate-400 leading-relaxed">{cat.desc}</p>

                  <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                    {cat.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-slate-200 text-sm">
                        <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-orange/20 grid place-items-center shrink-0">
                          <Check size={12} className="text-brand-orange" />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={`/services#${cat.id}`}
                    data-testid="service-detail-cta"
                    className="mt-8 inline-flex items-center gap-2 text-brand-orange font-semibold text-sm hover:gap-3 transition-all"
                  >
                    Explore {cat.title.toLowerCase()} in depth <ArrowUpRight size={16} />
                  </Link>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-10 flex justify-center">
          <Link to="/services" data-testid="services-view-all" className="btn-secondary">
            View all services <ArrowUpRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
